import BgNsxAddressSelector from '@britishgasenergy/react-nucleus/dist/nuc-components/NsxAddressSelector';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import axios from 'axios';

import { Error } from 'components/common/error/Error';
import { useMaskPII } from 'hooks/useMaskPII';
import { getAddressListThunk } from 'state/direct-debit/direct-debit-thunks';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { selectedAddress } from 'state/selected-address/selected-address-slice';
import { stringToBool } from 'utils/misc';
import { toNucleusAddress, fromNucleusAddress } from 'utils/nucAddressFormat';
import {
  selectDetails,
  selectPremisesData,
  setDirectDebitField,
  selectPremissesErrors,
  resetPremisesErrors,
  resetAddressSelector,
  resetSelectedAddress
} from 'state/direct-debit/direct-debit-slice';

export const testId = 'directDebitAddressTestId';

export const DirectDebitAddress = () => {
  const dispatch = useDispatch();
  const { mask } = useMaskPII();
  const errors = useSelector(selectPremissesErrors, shallowEqual);
  const supplyAddress = useSelector(selectedAddress, shallowEqual);
  const { isSupplyAddress, address, postcode } = useSelector(
    selectDetails,
    shallowEqual
  );
  const { addresses } = useSelector(selectPremisesData, shallowEqual);
  const nucAddresses = addresses?.map((address) => toNucleusAddress(address));
  const nucAddress = toNucleusAddress(address);

  const onPostcodeSelected = async (event) => {
    const {
      detail: { postcode }
    } = event;
    const source = axios.CancelToken.source();
    const requestConfig = { cancelToken: source.token };

    const callApis = async () => {
      dispatch(resetPremisesErrors());

      try {
        await dispatch(getAddressListThunk(postcode, requestConfig));

        dispatch(setDirectDebitField({ name: 'postcode', value: postcode }));
      } catch (e) {
        dispatch(setDirectDebitField({ name: 'addresses', value: [] }));
        e && console.error(e);
      }
    };

    return await callApis();
  };

  const onManualAddressClick = () => {
    dispatch(resetPremisesErrors());
  };

  const onAddressSelected = (event) => {
    const {
      target: { name, value: address }
    } = event;
    if (!address) {
      // means 'change postcode' clicked
      dispatch(resetAddressSelector());
      dispatch(resetSelectedAddress());
    } else {
      dispatch(
        setDirectDebitField({ name, value: fromNucleusAddress(address) })
      );
    }
  };

  const handleRadioChange = (event) => {
    const {
      target: { value, name }
    } = event;
    const isSupplyAddress = stringToBool(value);

    dispatch(setDirectDebitField({ name, value: isSupplyAddress }));

    if (isSupplyAddress) {
      dispatch(resetAddressSelector());
      dispatch(setDirectDebitField({ name: 'address', value: supplyAddress }));
    } else {
      dispatch(resetSelectedAddress());
    }
  };

  return (
    <>
      <NsRadioInput
        name="isSupplyAddress"
        onChange={handleRadioChange}
        value={isSupplyAddress}
        heading="Where are these details registered?"
        helper="Using the correct address"
        tipDetail={`If there's a different address on your bank account, please 
                    tell us what it is to avoid any issues setting up your 
                    Direct Debit.`}
        options={[
          {
            label: <span {...mask}>{supplyAddress?.displayName}</span>,
            value: 'true'
          },
          {
            label: 'A different address',
            value: 'false'
          }
        ]}
      />
      {isSupplyAddress === false && (
        <>
          <div {...mask}>
            <BgNsxAddressSelector
              name="address"
              onPostcodeSelected={onPostcodeSelected}
              onAddressSelected={onAddressSelected}
              onManualAddressClick={onManualAddressClick}
              addresses={nucAddresses ?? ''}
              selected-address={JSON.stringify(nucAddress ?? '')}
              postcode={postcode ?? ''}
              allowManualAddress
              required
            />
          </div>
          <Error errors={errors} />
        </>
      )}
    </>
  );
};
